"use client";

import {
  NodeResizer,
  NodeToolbar,
  Position,
  useStore,
  type NodeProps,
} from "@xyflow/react";
import { memo } from "react";
import { useSystemStore } from "@/lib/store";
import { useCanvasViewOnly } from "./canvas-mode";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";
import { MobileDetailsPill } from "./mobile-details-pill";

export type ZoneNodeData = {
  label?: string;
};

function ZoneNodeInner({ id, data, selected }: NodeProps) {
  const d = data as ZoneNodeData;
  const updateNode = useSystemStore((s) => s.updateNode);
  const viewOnly = useCanvasViewOnly();
  const isTouchLayout = useIsTouchLayout();
  const zoom = useStore((state) => state.transform[2]);
  const safeZoom = Math.max(zoom, 0.01);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "var(--color-zone-bg)",
        border: selected
          ? "1.5px solid var(--color-accent)"
          : "1.5px dashed var(--color-border-strong)",
        borderRadius: 16,
        position: "relative",
      }}
    >
      <NodeResizer
        isVisible={
          !viewOnly && selected && (!isTouchLayout || safeZoom >= 0.5)
        }
        minWidth={200}
        minHeight={140}
        autoScale={!isTouchLayout}
        lineStyle={{ stroke: "var(--color-accent)", strokeWidth: 1 }}
        handleStyle={{
          width: isTouchLayout ? 44 / safeZoom : 8,
          height: isTouchLayout ? 44 / safeZoom : 8,
          background: "var(--color-surface)",
          border: `${isTouchLayout ? 1.5 / safeZoom : 1.5}px solid var(--color-accent)`,
          borderRadius: isTouchLayout ? 3 / safeZoom : 2,
        }}
      />
      <div className="absolute -top-5 left-1 flex items-center">
        <input
          value={d.label ?? "Zone"}
          onChange={(e) => updateNode(id, { label: e.target.value })}
          readOnly={viewOnly}
          className="nodrag nopan nowheel bg-transparent outline-none text-[11px] font-medium tracking-tight w-auto px-0.5 -mx-0.5 rounded-[3px] hover:bg-[var(--color-surface-2)] focus:bg-[var(--color-surface)] focus:shadow-[0_0_0_1px_var(--color-accent)] read-only:hover:bg-transparent read-only:cursor-default"
          style={{
            minWidth: 60,
            width: `${Math.max((d.label ?? "Zone").length, 4) * 7}px`,
            color: "var(--color-fg-muted)",
            fontSize: isTouchLayout ? 16 : undefined,
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      {isTouchLayout && selected && (
        <NodeToolbar
          nodeId={id}
          isVisible
          position={Position.Bottom}
          offset={28}
          className="nodrag nopan"
          style={{ pointerEvents: "auto" }}
        >
          <MobileDetailsPill anchor={{ kind: "node", id }} />
        </NodeToolbar>
      )}
    </div>
  );
}

export const ZoneNodeView = memo(ZoneNodeInner);
